import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { TenantConfig } from '../../models/TenantConfig.model';
import { BusinessHour } from '../../models/business-hour.model';
import { TenantService } from '../../services/tenant.service';
import {
  MenuCategory,
  MenuItem,
  MenuItemVariant,
} from '../../models/menu.model';
import { filter, Subscription, timer } from 'rxjs';
import { MenuService } from '../../../features/user/menu.service';
import { TermsService } from '../../services/terms.service';
import { AnalyticsService } from '../../services/analytics.service';
import { Router } from '@angular/router';
import { CartItem, CartService } from '../../services/cart.service';
import {
  BusinessHoursCalculatorService,
  BusinessHoursMeta,
  DeliveryStatus,
  DayHours,
} from '../../services/business-hours-calculator.service';
import { HtmlEntityDecoderService } from '../../../shared/services/html-entity-decoder.service';

interface CartOption {
  id: number;
  extras: { id: number }[][];
}

@Component({
  selector: 'app-menu',
  standalone: false,
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss',
})
export class MenuComponent implements OnInit, OnDestroy {
  @ViewChild('categoryScroll', { static: false })
  categoryScroll?: ElementRef<HTMLDivElement>;

  restaurantAddress = '';
  restaurantInfo: TenantConfig | null = null;
  todayStoreHours?: BusinessHour;
  minimumOrderAmount = 0;
  smallOrderFee = 0;
  publicDeliveryRules: {
    minimumDeliveryAmount: number;
    deliveryCharge: number;
  } | null = null;
  public orderType: 'delivery' | 'collection' = 'delivery';
  openingHoursWeekly: DayHours[] = [];
  deliveryHoursWeekly: DayHours[] = [];
  pauseActive = false;
  pauseClosedCollection = false;
  pauseClosedDelivery = false;
  pauseUntilLabel: string | null = null;
  pauseModalType: 'delivery' | 'collection' | 'both' | null = null;
  private readonly ORDER_TYPE_KEY = 'waitron.orderType';
  showPauseOrdersModal = false;
  isRestaurantClosed = false;
  showDeliveryClosedModal = false;
  todayDeliveryHours?: BusinessHour;

  private businessHoursMeta: BusinessHoursMeta | null = null;

  businessHours: BusinessHour[] = [];
  menuCategories: MenuCategory[] = [];
  currentCategory!: string;
  private _sub = new Subscription();
  menuDescription: string = '';
  menuName: string = '';
  error: string | null = null;
  private pausePollSub?: Subscription;
  private lastItemListSent = '';
  openingTimes: { day: string; hours: string }[] = [];
  nextStoreOpening?: BusinessHour;
  private deliveryGuardInitialized = false;
  selectedItem: MenuItem | null = null;
  quantity = 1;
  selectedOptionGroups: {
    [groupName: string]: { [choiceName: string]: boolean };
  } = {};
  selectedVariant: MenuItemVariant | null = null;
  private _modalOpen = false;
  searchQuery = '';
  showRequiredErrors = false;
  private isProgrammaticCategoryScroll = false;
  private targetScrollY: number | null = null;
  private pendingCategory: string | null = null;

  menuLoaded = false;
  tenantLoaded = false;
  hoursLoaded = false;
  deliveryRulesLoaded = false;
  feesLoaded = false;

  constructor(
    private menuService: MenuService,
    private router: Router,
    private cartService: CartService,
    private tenantService: TenantService,
    private analytics: AnalyticsService,
    private termsService: TermsService,
    private businessHoursCalc: BusinessHoursCalculatorService,
    private htmlDecoder: HtmlEntityDecoderService,
  ) {}

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (this.isProgrammaticCategoryScroll) {
      if (this.targetScrollY !== null) {
        const distance = Math.abs(window.scrollY - this.targetScrollY);

        // unlock only when we're basically at the destination
        if (distance <= 8) {
          this.isProgrammaticCategoryScroll = false;
          this.targetScrollY = null;

          if (this.pendingCategory) {
            this.currentCategory = this.pendingCategory;
            this.scrollCategoryPillIntoView(this.pendingCategory);
            this.pendingCategory = null;
          }
        }
      }
      return;
    }

    const offsetTrigger = 188;

    for (const cat of this.menuCategories) {
      const el = document.getElementById(this.categoryDomId(cat.categoryName));
      if (!el) continue;

      const rect = el.getBoundingClientRect();

      if (rect.top <= offsetTrigger && rect.bottom > offsetTrigger) {
        if (this.currentCategory !== cat.categoryName) {
          this.currentCategory = cat.categoryName;
          this.sendViewItemList(this.currentCategory);
          this.scrollCategoryPillIntoView(cat.categoryName);
        }
        break;
      }
    }
  }

  ngOnInit(): void {
    if (this.menuCategories.length) {
      this.currentCategory = this.menuCategories[0].categoryName;
    }

    const saved = this.getSavedOrderType();
    if (saved) {
      this.orderType = saved;
    } else {
      // Optionally store the default the first time
      this.saveOrderType(this.orderType);
    }

    this._sub.add(
      this.menuService.getMenus().subscribe({
        next: (resp: any) => {
          if (resp.status === 'success') {
            const decodedMenuMenus = this.htmlDecoder.decodeDeep(
              resp.menuMenus || [],
            );
            const firstMenu = decodedMenuMenus[0];

            this.menuCategories = (firstMenu?.categories || []).map(
              (cat: any) => ({
                ...cat,
                items: (cat.items || []).map((item: any) => ({
                  ...item,
                  basePrice:
                    item.basePrice != null && item.basePrice !== ''
                      ? Number(item.basePrice)
                      : 0,
                  options: (item.options || []).map((link: any) => {
                    const group = link.optionGroup ?? link;

                    return {
                      ...group,
                      minSelect: Number(group.minSelect ?? 0),
                      maxSelect: Number(group.maxSelect ?? 0),
                      isRequired: Number(group.isRequired ?? 0),
                      extras: (group.extras || []).map((extra: any) => ({
                        ...extra,
                        priceAdjustment:
                          extra.priceAdjustment != null &&
                          extra.priceAdjustment !== ''
                            ? Number(extra.priceAdjustment)
                            : 0,
                      })),
                    };
                  }),
                })),
              }),
            );

            if (this.menuCategories.length) {
              this.currentCategory = this.menuCategories[0].categoryName;

              if (this.currentCategory) {
                this.sendViewItemList(this.currentCategory);
              }
            }

            this.menuDescription = firstMenu?.menuDescription || '';
            this.menuName = firstMenu?.menuName || '';

            this.menuLoaded = true;

            this.termsService.prefetchLatestTerms().subscribe();
          } else {
            this.error = 'Error fetching menu';
            this.menuLoaded = true;
          }
        },
        error: (err) => {
          this.error = err.message || 'Error fetching menu';
          this.menuLoaded = true;
        },
      }),
    );

    this._sub.add(
      this.tenantService.tenantConfig$.subscribe((cfg) => {
        if (!cfg) return;

        const decodedCfg = this.htmlDecoder.decodeDeep(cfg);

        this.restaurantInfo = decodedCfg;
        this.minimumOrderAmount = decodedCfg.tenant.minimumOrderAmount;
        this.smallOrderFee = decodedCfg.tenant.smallOrderFee;
        this.restaurantAddress = this.htmlDecoder.decode(
          this.tenantService.shopAddressDisplay ?? '',
        );

        this.tenantLoaded = true;
      }),
    );

    this._sub.add(
      this.tenantService.businessHoursMeta$.subscribe((meta) => {
        this.businessHoursMeta = meta;
      }),
    );

    // subscribe to hours
    this._sub.add(
      this.tenantService.businessHours$.subscribe((hours) => {
        if (!hours) return;

        this.businessHours = hours;

        const timeZone = this.businessHoursCalc.getTimezone(
          this.businessHoursMeta,
        );
        const now = this.businessHoursCalc.getNow(this.businessHoursMeta);

        const todayHours = this.businessHoursCalc.getTodayHours(
          hours,
          timeZone,
          now,
        );

        this.todayStoreHours = todayHours.todayStoreHours;
        this.todayDeliveryHours = todayHours.todayDeliveryHours;

        this.isRestaurantClosed = this.businessHoursCalc.getClosedFlag(
          hours,
          'store',
          timeZone,
          now,
        );

        this.nextStoreOpening = this.isRestaurantClosed
          ? this.businessHoursCalc.getNextOpening(hours, 'store', now)
          : undefined;

        this.openingHoursWeekly =
          this.businessHoursCalc.toDayHoursFromBusinessHours(
            hours,
            'store',
            timeZone,
            now,
          );

        this.deliveryHoursWeekly =
          this.businessHoursCalc.toDayHoursFromBusinessHours(
            hours,
            'delivery',
            timeZone,
            now,
          );

        this.openingTimes = this.businessHours
          .filter((h) => h.hoursCategory === 'store')
          .sort((a, b) => a.generatedDate.localeCompare(b.generatedDate))
          .slice(0, 7)
          .map((h) => ({
            day: h.openingDay,
            hours: this.businessHoursCalc.isClosedVal(h.isClosed)
              ? 'Closed'
              : `${this.businessHoursCalc.formatTime(h.openTime)} – ${this.businessHoursCalc.formatTime(h.closeTime)}${
                  this.businessHoursCalc.isOvernightHours(h) ? ' next day' : ''
                }`,
          }));

        this.enforceDeliveryGuard(true);

        this.hoursLoaded = true;
      }),
    );

    this._sub.add(
      this.menuService.getPublicDeliveryRules().subscribe({
        next: (res) => {
          if (res?.status === 'success' && res?.deliveryRules) {
            const rule = res.deliveryRules;
            this.publicDeliveryRules = {
              minimumDeliveryAmount: Number(rule.minimumDeliveryAmount ?? 0),
              deliveryCharge: Number(rule.deliveryCharge ?? 0),
            };
          } else {
            this.publicDeliveryRules = null;
          }

          this.deliveryRulesLoaded = true;
        },
        error: (err) => {
          console.warn('Failed to fetch public delivery rules', err);
          this.publicDeliveryRules = null;
          this.deliveryRulesLoaded = true;
        },
      }),
    );

    this._sub.add(
      this.tenantService.tenantFees$
        .pipe(filter((fees) => fees !== null))
        .subscribe(() => {
          this.feesLoaded = true;
        }),
    );

    this.refreshPauseStatusPublic();

    this.pausePollSub = timer(90_000, 90_000).subscribe(() => {
      this.refreshPauseStatusPublic();
    });
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
    this.pausePollSub?.unsubscribe();
  }

  get fees(): { minOrder: number; deliveryFee: number; serviceFee: number } {
    return {
      minOrder: this.publicDeliveryRules?.minimumDeliveryAmount ?? 0,
      deliveryFee: this.publicDeliveryRules?.deliveryCharge ?? 0,
      serviceFee: this.serviceFee,
    };
  }

  onOrderTypeChange(newType: 'delivery' | 'collection') {
    // If delivery is paused, keep UI honest: modal is about DELIVERY being paused
    if (
      newType === 'delivery' &&
      this.pauseActive &&
      this.pauseClosedDelivery
    ) {
      this.pauseModalType = 'delivery';
      this.orderType = 'collection';
      this.saveOrderType('collection');
      this.showPauseOrdersModal = true;
      return;
    }

    if (
      newType === 'collection' &&
      this.pauseActive &&
      this.pauseClosedCollection
    ) {
      this.pauseModalType = 'collection';

      if (!this.pauseClosedDelivery) {
        this.orderType = 'delivery';
        this.saveOrderType('delivery');
        this.showPauseOrdersModal = true;
        return;
      }

      // both paused
      this.pauseModalType = 'both';
      this.orderType = 'collection';
      this.saveOrderType('collection');
      this.showPauseOrdersModal = true;
      return;
    }

    // existing delivery-hours guard...
    if (newType === 'delivery' && !this.isDeliveryOpenNow) {
      if (!this.isRestaurantClosed) this.showDeliveryClosedModal = true;
      this.orderType = 'collection';
      this.saveOrderType('collection');
      return;
    }

    this.orderType = newType;
    this.saveOrderType(newType);

    // keep modal in sync
    this.pauseModalType = this.computePausedType();
    this.showPauseOrdersModal = this.isOrderingPausedForCurrentType;
  }

  get serviceFee(): number {
    return this.tenantService.serviceFee; // pounds (e.g. 0.5)
  }

  private saveOrderType(val: 'delivery' | 'collection'): void {
    try {
      sessionStorage.setItem(this.ORDER_TYPE_KEY, val);
    } catch {
      // ignore if unavailable
    }
  }

  get isDeliveryOpenNow(): boolean {
    if (!this.businessHours.length) return true;

    const timeZone = this.businessHoursCalc.getTimezone(this.businessHoursMeta);
    const now = this.businessHoursCalc.getNow(this.businessHoursMeta);

    return this.businessHoursCalc.isOpenNow(
      this.businessHours,
      'delivery',
      timeZone,
      now,
    );
  }

  private computePausedType(): 'delivery' | 'collection' | 'both' | null {
    if (!this.pauseActive) return null;
    if (this.pauseClosedDelivery && this.pauseClosedCollection) return 'both';
    if (this.pauseClosedDelivery) return 'delivery';
    if (this.pauseClosedCollection) return 'collection';
    return null;
  }

  get isOrderingPausedForCurrentType(): boolean {
    if (!this.pauseActive) return false;

    if (this.orderType === 'delivery') return this.pauseClosedDelivery;
    return this.pauseClosedCollection;
  }

  private getSavedOrderType(): 'delivery' | 'collection' | null {
    try {
      const val = sessionStorage.getItem(this.ORDER_TYPE_KEY);
      if (val === 'delivery' || val === 'collection') return val;
      return null;
    } catch {
      return null; // e.g. SSR or storage blocked
    }
  }

  private sendViewItemList(categoryName: string) {
    if (!categoryName || this.lastItemListSent === categoryName) return;
    const items = this.getItemsForCategory(categoryName)
      .slice(0, 20) // keep payload light
      .map((i) => this.gaItemForMenuItem(i, categoryName));

    this.analytics.event('view_item_list', {
      item_list_name: categoryName,
      items,
    });

    this.lastItemListSent = categoryName;
  }

  private enforceDeliveryGuard(initial: boolean = false): void {
    if (!this.deliveryGuardInitialized && this.todayDeliveryHours) {
      this.deliveryGuardInitialized = true;
    }
    // Only enforce when we actually have hours loaded
    if (!this.todayDeliveryHours) return;

    if (!this.isDeliveryOpenNow && this.orderType === 'delivery') {
      this.orderType = 'collection';
      this.saveOrderType('collection');
      // We silently switch on initial load (no modal) per your spec.
      // If you prefer a toast/banner here, you can trigger it conditionally when `initial === true`.
    }
  }

  private refreshPauseStatusPublic() {
    this.menuService.viewOrderPausePublic().subscribe({
      next: (res: any) => {
        const dto = this.extractPauseDto(res);

        if (!dto) {
          this.pauseActive = false;
          this.pauseClosedCollection = false;
          this.pauseClosedDelivery = false;
          this.pauseUntilLabel = null;
          this.showPauseOrdersModal = false;
          this.pauseModalType = null;
          return;
        }

        this.pauseActive = true;
        this.pauseClosedCollection = this.isClosedVal(dto.isClosedCollection);
        this.pauseClosedDelivery = this.isClosedVal(dto.isClosedDelivery);
        this.pauseUntilLabel = this.formatTimeHHmm(dto.pauseUntil ?? null);

        this.pauseModalType = this.computePausedType();

        // ✅ show modal if current type is paused OR all paused
        this.showPauseOrdersModal = this.isOrderingPausedForCurrentType;
      },
      error: () => {
        this.pauseActive = false;
        this.pauseClosedCollection = false;
        this.pauseClosedDelivery = false;
        this.pauseUntilLabel = null;
        this.showPauseOrdersModal = false;
        this.pauseModalType = null;
      },
    });
  }

  private getItemsForCategory(categoryName: string): MenuItem[] {
    const cat: any = this.menuCategories.find(
      (c) => c.categoryName === categoryName,
    );
    // Your API usually returns either cat.items or cat.menuItems
    return (cat?.items as MenuItem[]) || (cat?.menuItems as MenuItem[]) || [];
  }

  private gaItemForMenuItem(
    item: MenuItem,
    categoryName?: string,
    variant?: MenuItemVariant,
  ) {
    const basePrice =
      variant?.price ?? (item as any).basePrice ?? (item as any).price ?? 0;
    return {
      item_id: String((item as any).itemId ?? (item as any).id),
      item_name: (item as any).itemName ?? (item as any).name,
      item_category: categoryName,
      item_variant: variant?.name,
      price: basePrice,
      quantity: 1,
    };
  }

  private extractPauseDto(res: any) {
    const list = res?.data;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list[0] ?? null;
  }

  private formatTimeHHmm(time: string | null | undefined): string | null {
    if (!time) return null;
    const m = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(time.trim());
    if (!m) return null;
    return `${m[1]}:${m[2]}`;
  }

  onCategoryPillClick(categoryName: string) {
    this.analytics.event('menu_category_click', {
      category_name: categoryName,
    });

    this.scrollToCategory(categoryName);
  }

  scrollToCategory(categoryName: string): void {
    const element = document.getElementById(this.categoryDomId(categoryName));
    const offset = 188;

    if (!element) return;

    const y = window.scrollY + element.getBoundingClientRect().top - offset;

    this.currentCategory = categoryName;
    this.pendingCategory = categoryName;
    this.targetScrollY = y;
    this.isProgrammaticCategoryScroll = true;

    this.scrollCategoryPillIntoView(categoryName);
    this.sendViewItemList(categoryName);

    window.scrollTo({
      top: y,
      behavior: 'smooth',
    });
  }

  scrollCategoryRow(direction: 'left' | 'right'): void {
    const el = this.categoryScroll?.nativeElement;
    if (!el) return;

    const scrollAmount = 180;

    el.scrollBy({
      left: direction === 'right' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  }

  openModal(item: MenuItem): void {
    this.showRequiredErrors = false;
    if (this.isOrderingDisabled) return;

    this.selectedItem = item;
    this.quantity = 1;
    this.selectedOptionGroups = {};

    if (item.options?.length) {
      item?.options.forEach((group) => {
        this.selectedOptionGroups[group.groupName] = {};
        group.extras.forEach((extra) => {
          this.selectedOptionGroups[group.groupName][extra.extraName] = false;
        });
      });
    }

    if (item.variants?.length) {
      this.selectedVariant =
        item.variants.find((v) => v.default) || item.variants[0];
    } else {
      this.selectedVariant = null;
    }

    this.analytics.event('select_item', {
      item_list_name: this.currentCategory,
      items: [this.gaItemForMenuItem(item, this.currentCategory)],
    });

    this.analytics.event('view_item', {
      items: [
        this.gaItemForMenuItem(
          item,
          this.currentCategory,
          this.selectedVariant || undefined,
        ),
      ],
    });

    this.modalOpen = true;
  }

  get isOrderingDisabled(): boolean {
    // closed OR paused for their chosen order type
    return this.isRestaurantClosed || this.isOrderingPausedForCurrentType;
  }

  get modalOpen() {
    return this._modalOpen;
  }

  set modalOpen(val: boolean) {
    this._modalOpen = val;
    document.body.style.overflow = val ? 'hidden' : '';
  }

  handleCheckout() {
    if (this.isOrderingDisabled) {
      this.showPauseOrdersModal = true;
      return;
    }

    this.analytics.event('begin_checkout_click', {
      source: 'menu_cart',
      order_type: this.orderType,
    });

    this.router.navigate(['/checkout']);
  }

  /** called when the user clicks backdrop / ✖ on the item-detail modal */
  closeDetailModal(): void {
    const item = this.selectedItem;
    this.modalOpen = false;
    this.showRequiredErrors = false;

    if (item) {
      this.analytics.event('menu_item_modal_close', {
        item_id: String((item as any).itemId ?? ''),
        item_name: (item as any).itemName ?? '',
        item_list_name: this.currentCategory,
      });
    }

    setTimeout(() => {
      this.selectedItem = null;
    }, 200);
  }

  onVariantChanged(variant: MenuItemVariant) {
    if (!this.selectedItem) return;

    this.analytics.event('select_item_variant', {
      item_list_name: this.currentCategory,
      item_id: String(this.selectedItem.itemId),
      item_name: this.selectedItem.itemName,
      item_variant: variant.name,
      price: variant.price,
    });
  }

  public groupMissingRequired(group: any) {
    return this.showRequiredErrors && this.isGroupMissingRequired(group);
  }

  private isGroupMissingRequired(group: any): boolean {
    const minReq = this.getGroupMinRequired(group);
    if (minReq <= 0) return false;
    const selectedCount = this.countSelectedInGroup(group.groupName);
    return selectedCount < minReq;
  }

  private getGroupMinRequired(group: any): number {
    // Prefer explicit minSelect if present
    const min = Number(group?.minSelect ?? 0);
    if (min > 0) return min;

    // Otherwise fall back to isRequired flag (means at least 1)
    return this.isTruthy(group?.isRequired) ? 1 : 0;
  }

  private countSelectedInGroup(groupName: string): number {
    const selections = this.selectedOptionGroups?.[groupName] || {};
    return Object.values(selections).filter(Boolean).length;
  }

  private isTruthy(v: any): boolean {
    return v === 1 || v === '1' || v === true;
  }

  public groupMinRequired(group: any) {
    return this.getGroupMinRequired(group);
  }

  isGroupMaxed(groupName: string): boolean {
    const group = this.selectedItem?.options?.find(
      (g) => g.groupName === groupName,
    );
    if (!group || !group.maxSelect) return false;

    const selections = this.selectedOptionGroups[groupName] || {};
    const selectedCount = Object.values(selections).filter((v) => v).length;

    return selectedCount >= group.maxSelect;
  }

  onExtraToggled(groupName: string, extra: any) {
    const selected =
      !!this.selectedOptionGroups?.[groupName]?.[extra.extraName];

    if (this.showRequiredErrors) {
      this.showRequiredErrors = this.missingRequiredOptions;
    }

    this.analytics.event('select_item_extra', {
      item_id: String(this.selectedItem?.itemId ?? ''),
      item_name: this.selectedItem?.itemName ?? '',
      group_name: groupName,
      extra_name: extra.extraName,
      extra_id: extra.extraId,
      price_adjustment: Number(extra.priceAdjustment ?? 0),
      selected,
    });
  }

  get calculatedModalPrice(): number {
    const basePrice = Number(
      this.selectedVariant?.price ?? this.selectedItem?.basePrice ?? 0,
    );

    const groups: any[] = (this.selectedItem as any)?.options ?? [];

    const extrasTotal = groups.reduce((sum, group) => {
      const selectedChoices = this.selectedOptionGroups?.[group.groupName];
      if (!selectedChoices) return sum;

      const extras = group.extras ?? [];
      const groupSum = extras.reduce((groupTotal: number, extra: any) => {
        return selectedChoices[extra.extraName]
          ? groupTotal + Number(extra.priceAdjustment ?? 0)
          : groupTotal;
      }, 0);

      return sum + groupSum;
    }, 0);

    return (basePrice + extrasTotal) * this.quantity;
  }

  decreaseQty(): void {
    if (this.quantity <= 1) return;

    this.quantity--;
    this.analytics.event('menu_qty_change', {
      item_id: String(this.selectedItem?.itemId ?? ''),
      item_name: this.selectedItem?.itemName ?? '',
      qty: this.quantity,
      direction: 'dec',
    });
  }

  increaseQty(): void {
    this.quantity++;
    this.analytics.event('menu_qty_change', {
      item_id: String(this.selectedItem?.itemId ?? ''),
      item_name: this.selectedItem?.itemName ?? '',
      qty: this.quantity,
      direction: 'inc',
    });
  }

  get canAddToBasket(): boolean {
    return !this.isOrderingDisabled && !this.missingRequiredOptions;
  }

  get missingRequiredOptions(): boolean {
    const groups: any[] = (this.selectedItem as any)?.options ?? [];
    if (!this.selectedItem || !groups.length) return false;

    // ignore disabled extras/groups if you want later; for now just enforce min selects
    return groups.some((g) => this.isGroupMissingRequired(g));
  }

  addToBasket(): void {
    if (!this.selectedItem) return;
    if (this.isOrderingDisabled) return;
    if (this.missingRequiredOptions) {
      this.showRequiredErrors = true;
      return;
    }
    if (!this.canAddToBasket) return;

    const selectedPrice = Number(
      this.selectedVariant?.price ?? this.selectedItem.basePrice ?? 0,
    );

    // Build extras array
    const extras: {
      group: string;
      choice: string;
      price: number;
      id: string;
    }[] = [];

    this.selectedItem.options?.forEach((group) => {
      const groupSelections = this.selectedOptionGroups[group.groupName];
      if (groupSelections) {
        group.extras.forEach((extra) => {
          if (groupSelections[extra.extraName]) {
            extras.push({
              group: group.groupName,
              choice: extra.extraName,
              price: Number(extra.priceAdjustment ?? 0),
              id: extra.extraId,
            });
          }
        });
      }
    });

    // 2) Build the payload-friendly options array:
    const payloadOptions: CartOption[] = [];
    this.selectedItem.options?.forEach((group) => {
      const sel = this.selectedOptionGroups[group.groupName] || {};
      const chosen = (group.extras || [])
        .filter((extra) => sel[extra.extraName])
        .map((extra) => extra.extraId);

      if (chosen.length) {
        payloadOptions.push({
          id: Number(group.optionId),
          extras: chosen.map((extId) => [{ id: Number(extId) }]),
        });
      }
    });

    // Calculate total price including extras
    const extrasTotal = extras.reduce((sum, extra) => sum + extra.price, 0);

    const lineId = this.buildCartLineId(
      Number(this.selectedItem.itemId),
      this.selectedVariant?.name ?? null,
      payloadOptions,
    );

    const cartItem: CartItem & { options?: CartOption[]; itemId: number } = {
      id: lineId,
      itemId: Number(this.selectedItem.itemId),
      name: this.selectedVariant?.name
        ? `${this.selectedItem.itemName} (${this.selectedVariant.name})`
        : this.selectedItem.itemName,
      price: selectedPrice + extrasTotal,
      quantity: this.quantity,
      imageUrl: this.selectedItem.imageUrl ?? '',
      extras,
      options: payloadOptions,
    };

    this.cartService.addToCart(cartItem);
    this.analytics.event('add_to_cart', {
      currency: 'GBP',
      value: cartItem.price * cartItem.quantity,
      item_list_name: this.currentCategory,
      items: [
        {
          item_id: String(this.selectedItem.itemId),
          item_name: this.selectedVariant?.name
            ? `${this.selectedItem.itemName} (${this.selectedVariant.name})`
            : this.selectedItem.itemName,
          item_category: this.currentCategory,
          item_variant: this.selectedVariant?.name,
          price: cartItem.price,
          quantity: cartItem.quantity,
        },
      ],
    });
    this.closeDetailModal();
  }

  private buildCartLineId(
    itemId: number,
    variantName: string | null,
    payloadOptions: CartOption[],
  ): string {
    // Normalize options so the same selections always generate the same key
    const normalized = (payloadOptions || [])
      .map((g) => ({
        id: Number(g.id),
        extras: (g.extras || [])
          .flat()
          .map((e) => Number(e.id))
          .sort((a, b) => a - b),
      }))
      .sort((a, b) => a.id - b.id);

    const variantKey = variantName ? `v:${variantName}` : 'v:base';
    return `${itemId}|${variantKey}|${JSON.stringify(normalized)}`;
  }

  onCloseDeliveryClosedModal() {
    this.showDeliveryClosedModal = false;
    // ensure header shows the correct tab
    this.orderType = 'collection';
    this.saveOrderType('collection');
  }

  get deliveryStatus(): DeliveryStatus {
    const timeZone = this.businessHoursCalc.getTimezone(this.businessHoursMeta);
    const now = this.businessHoursCalc.getNow(this.businessHoursMeta);

    const activeDeliveryHour = this.businessHoursCalc.getActiveBusinessHourNow(
      this.businessHours,
      'delivery',
      timeZone,
      now,
    );

    if (activeDeliveryHour) {
      this.todayDeliveryHours = activeDeliveryHour;
    }

    return this.businessHoursCalc.getDeliveryStatus(
      this.businessHours,
      timeZone,
      now,
    );
  }

  onClosePauseOrdersModal() {
    this.showPauseOrdersModal = false;
    this.pauseModalType = null;
  }

  get isAllOrderingPaused(): boolean {
    return (
      this.pauseActive && this.pauseClosedCollection && this.pauseClosedDelivery
    );
  }

  private scrollCategoryPillIntoView(categoryName: string): void {
    requestAnimationFrame(() => {
      const container = this.categoryScroll?.nativeElement;
      if (!container) return;

      const pill = container.querySelector(
        `[data-category="${CSS.escape(categoryName)}"]`,
      ) as HTMLButtonElement | null;

      if (!pill) return;

      pill.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    });
  }

  get deliveryToggleNote(): string {
    if (this.deliveryStatus === 'before' && this.todayDeliveryHours?.openTime) {
      return `Opens ${this.formatTimeHHmm(this.todayDeliveryHours.openTime) ?? ''}`;
    }

    return '';
  }

  get isDeliverySelectable(): boolean {
    if (this.pauseActive && this.pauseClosedDelivery) return false;
    return this.isDeliveryOpenNow;
  }

  get cartDeliveryToggleLabel(): string {
    if (this.pauseActive && this.pauseClosedDelivery) {
      return this.pauseUntilLabel
        ? `Paused until ${this.pauseUntilLabel}`
        : 'Unavailable';
    }

    if (this.deliveryStatus === 'closedAllDay') {
      return 'Closed today';
    }

    if (this.deliveryStatus === 'before' && this.todayDeliveryHours?.openTime) {
      return `Opens ${this.formatTimeHHmm(this.todayDeliveryHours.openTime)}`;
    }

    if (this.deliveryStatus === 'after') {
      return 'Closed now';
    }

    return '';
  }

  get loading(): boolean {
    return !(
      this.menuLoaded &&
      this.tenantLoaded &&
      this.hoursLoaded &&
      this.deliveryRulesLoaded &&
      this.feesLoaded
    );
  }

  formatTime(time: string | null): string {
    return this.businessHoursCalc.formatTime(time);
  }

  isClosedVal(v: any): boolean {
    return this.businessHoursCalc.isClosedVal(v);
  }

  isOvernightDisplay(hour: BusinessHour | undefined): boolean {
    return this.businessHoursCalc.isOvernightHours(hour);
  }

  categoryDomId(categoryName: string): string {
    return `category-${categoryName
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')}`;
  }

  get allergensNote(): string {
    const name = this.restaurantInfo?.tenant?.companyName || 'the restaurant';

    return `You can call ${name} to ask about their ingredients and allergen information, production or cooking methods.`;
  }
}
